<!-- PaymentCard.vue -->
<template>
  <div class="payment-container">
    <h3>{{ $t('payment.title') }}</h3>

    <form @submit.prevent="processPayment" class="payment-form">
      <!-- 卡号输入 -->
      <div class="form-group">
        <label for="cardNumber">{{ $t('payment.cardNumber') }}</label>
        <input v-model="cardData.number" @input="formatCardNumber" @blur="validateCardNumber" type="text"
          id="cardNumber" :placeholder="$t('payment.cardNumberPlaceholder')" maxlength="19"
          :class="{ 'error': errors.number }" />
        <div class="error-message" v-if="errors.number">{{ errors.number }}</div>

        <!-- 卡组织图标 -->
        <div class="card-icons">
          <div v-for="card in supportedCards" :key="card.type"
            :class="['card-icon', { 'active': detectedCardType === card.type }]" :title="card.name">
            {{ card.displayName }}
          </div>
        </div>
      </div>

      <!-- 持卡人姓名 -->
      <div class="form-group">
        <label for="cardHolder">{{ $t('payment.cardHolder') }}</label>
        <input v-model="cardData.holder" @blur="validateCardHolder" type="text" id="cardHolder"
          :placeholder="$t('payment.cardHolderPlaceholder')" :class="{ 'error': errors.holder }" />
        <div class="error-message" v-if="errors.holder">{{ errors.holder }}</div>
      </div>

      <!-- 有效期和CVV -->
      <div class="form-row">
        <div class="form-group">
          <label>{{ $t('payment.expiryDate') }}</label>
          <div class="expiry-inputs">
            <select v-model="cardData.expiryMonth" @change="validateExpiry">
              <option value="">{{ $t('payment.month') }}</option>
              <option v-for="month in months" :key="month" :value="month">
                {{ month }}
              </option>
            </select>
            <select v-model="cardData.expiryYear" @change="validateExpiry">
              <option value="">{{ $t('payment.year') }}</option>
              <option v-for="year in years" :key="year" :value="year">
                {{ year }}
              </option>
            </select>
          </div>
          <div class="error-message" v-if="errors.expiry">{{ errors.expiry }}</div>
        </div>

        <div class="form-group">
          <label for="cvv">{{ $t('payment.cvv') }}</label>
          <input v-model="cardData.cvv" @input="validateCVV" type="text" id="cvv"
            :placeholder="$t('payment.cvvPlaceholder')" maxlength="4" :class="{ 'error': errors.cvv }" />
          <div class="error-message" v-if="errors.cvv">{{ errors.cvv }}</div>
        </div>
      </div>

      <!-- 卡片类型 -->
      <div class="form-group">
        <label for="cardType">{{ $t('payment.cardType') }}</label>
        <select v-model="cardData.type" @change="validateCardType" id="cardType" :class="{ 'error': errors.type }">
          <option value="">{{ $t('payment.selectCardType') }}</option>
          <option value="credit">{{ $t('payment.creditCard') }}</option>
          <option value="debit">{{ $t('payment.debitCard') }}</option>
        </select>
        <div class="error-message" v-if="errors.type">{{ errors.type }}</div>
      </div>

      <!-- 支付按钮 -->
      <button type="submit" class="submit-btn" :disabled="isProcessing || !isFormValid"
        :class="{ 'processing': isProcessing }">
        <span v-if="!isProcessing">{{ $t('payment.payNow') }}</span>
        <span v-else>{{ $t('payment.processing') }}</span>
      </button>
    </form>

  </div>
</template>

<script>
export default {
  name: 'PaymentCard',

  data() {
    return {
      cardData: {
        number: '',
        holder: '',
        expiryMonth: '',
        expiryYear: '',
        cvv: '',
        type: ''
      },

      errors: {
        number: '',
        holder: '',
        expiry: '',
        cvv: '',
        type: ''
      },

      isProcessing: false,
      paymentResult: {
        success: false,
        message: ''
      },

      detectedCardType: '',

      // 支持的卡类型配置
      cardTypes: {
        visa: {
          pattern: /^4/,
          lengths: [16],
          cvvLength: 3,
          name: 'Visa'
        },
        mastercard: {
          pattern: /^5[1-5]/,
          lengths: [16],
          cvvLength: 3,
          name: 'Mastercard'
        },
        amex: {
          pattern: /^3[47]/,
          lengths: [15],
          cvvLength: 4,
          name: 'American Express'
        },
        discover: {
          pattern: /^6(?:011|5)/,
          lengths: [16],
          cvvLength: 3,
          name: 'Discover'
        },
        unionpay: {
          pattern: /^62/,
          lengths: [16, 17, 18, 19],
          cvvLength: 3,
          name: '银联'
        },
        jcb: {
          pattern: /^35/,
          lengths: [16],
          cvvLength: 3,
          name: 'JCB'
        }
      },

      months: [],
      years: []
    };
  },

  computed: {
    // 支持的卡组织列表
    supportedCards() {
      return Object.keys(this.cardTypes).map(type => ({
        type,
        name: this.cardTypes[type].name,
        displayName: this.getCardDisplayName(type)
      }));
    },

    // 表单验证状态
    isFormValid() {
      return (
        !this.errors.number &&
        !this.errors.holder &&
        !this.errors.expiry &&
        !this.errors.cvv &&
        !this.errors.type &&
        this.cardData.number &&
        this.cardData.holder &&
        this.cardData.expiryMonth &&
        this.cardData.expiryYear &&
        this.cardData.cvv &&
        this.cardData.type
      );
    }
  },

  mounted() {
    this.generateDateOptions();
  },

  methods: {
    // 生成月份和年份选项
    generateDateOptions() {
      // 月份
      for (let i = 1; i <= 12; i++) {
        this.months.push(i.toString().padStart(2, '0'));
      }

      // 年份（当前年份 + 未来10年）
      const currentYear = new Date().getFullYear();
      for (let i = 0; i < 10; i++) {
        this.years.push((currentYear + i).toString());
      }
    },

    // 格式化卡号（每4位加空格）
    formatCardNumber() {
      let value = this.cardData.number.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
      let formattedValue = '';

      for (let i = 0; i < value.length; i++) {
        if (i > 0 && i % 4 === 0) {
          formattedValue += ' ';
        }
        formattedValue += value[i];
      }

      this.cardData.number = formattedValue;
      this.detectCardType();
    },

    // 检测卡类型
    detectCardType() {
      const cleanNumber = this.cardData.number.replace(/\s+/g, '');
      this.detectedCardType = '';

      for (const [type, config] of Object.entries(this.cardTypes)) {
        if (config.pattern.test(cleanNumber)) {
          this.detectedCardType = type;
          break;
        }
      }
    },

    // 获取卡组织显示名称
    getCardDisplayName(cardType) {
      const names = {
        visa: 'VISA',
        mastercard: 'MC',
        amex: 'AMEX',
        discover: 'DISC',
        unionpay: '银联',
        jcb: 'JCB'
      };
      return names[cardType] || cardType.toUpperCase();
    },

    // 验证卡号
    validateCardNumber() {
      const cleanNumber = this.cardData.number.replace(/\s+/g, '');

      if (!cleanNumber) {
        this.errors.number = this.$t('validation.cardNumberRequired');
        return false;
      }

      if (!/^\d+$/.test(cleanNumber)) {
        this.errors.number = this.$t('validation.cardNumberDigitsOnly');
        return false;
      }

      if (!this.detectedCardType) {
        this.errors.number = this.$t('validation.unsupportedCardType');
        return false;
      }

      const expectedLengths = this.cardTypes[this.detectedCardType].lengths;
      if (!expectedLengths.includes(cleanNumber.length)) {
        this.errors.number = this.$t('validation.invalidCardLength', {
          lengths: expectedLengths.join(' or ')
        });
        return false;
      }

      if (!this.luhnCheck(cleanNumber)) {
        this.errors.number = this.$t('validation.invalidCardNumber');
        return false;
      }

      this.errors.number = '';
      return true;
    },

    // 验证持卡人姓名
    validateCardHolder() {
      if (!this.cardData.holder.trim()) {
        this.errors.holder = this.$t('validation.cardHolderRequired');
        return false;
      }

      this.errors.holder = '';
      return true;
    },

    // 验证有效期
    validateExpiry() {
      if (!this.cardData.expiryMonth || !this.cardData.expiryYear) {
        this.errors.expiry = this.$t('validation.expiryRequired');
        return false;
      }

      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;

      const expiryDate = new Date(
        parseInt(this.cardData.expiryYear),
        parseInt(this.cardData.expiryMonth) - 1
      );

      if (expiryDate < new Date(currentYear, currentMonth - 1)) {
        this.errors.expiry = this.$t('validation.cardExpired');
        return false;
      }

      this.errors.expiry = '';
      return true;
    },

    // 验证CVV
    validateCVV() {
      if (!this.cardData.cvv) {
        this.errors.cvv = this.$t('validation.cvvRequired');
        return false;
      }

      if (!/^\d+$/.test(this.cardData.cvv)) {
        this.errors.cvv = this.$t('validation.cvvDigitsOnly');
        return false;
      }

      let expectedLength = 3;
      if (this.detectedCardType && this.cardTypes[this.detectedCardType]) {
        expectedLength = this.cardTypes[this.detectedCardType].cvvLength;
      }

      if (this.cardData.cvv.length !== expectedLength) {
        this.errors.cvv = this.$t('validation.invalidCvvLength', { length: expectedLength });
        return false;
      }

      this.errors.cvv = '';
      return true;
    },

    // 验证卡片类型
    validateCardType() {
      if (!this.cardData.type) {
        this.errors.type = this.$t('validation.cardTypeRequired');
        return false;
      }

      this.errors.type = '';
      return true;
    },

    // Luhn算法验证卡号
    luhnCheck(cardNumber) {
      let sum = 0;
      let isEven = false;

      for (let i = cardNumber.length - 1; i >= 0; i--) {
        let digit = parseInt(cardNumber[i]);

        if (isEven) {
          digit *= 2;
          if (digit > 9) {
            digit -= 9;
          }
        }

        sum += digit;
        isEven = !isEven;
      }

      return sum % 10 === 0;
    },

    // 处理支付
    async processPayment() {
      // 验证所有字段
      const validations = [
        this.validateCardNumber(),
        this.validateCardHolder(),
        this.validateExpiry(),
        this.validateCVV(),
        this.validateCardType()
      ];

      if (validations.includes(false)) {
        return;
      }

      // this.isProcessing = true;

      const paymentData = {
        cardNumber: this.cardData.number.replace(/\s+/g, ''),
        cardHolder: this.cardData.holder,
        expiryMonth: this.cardData.expiryMonth,
        expiryYear: this.cardData.expiryYear,
        cvv: this.cardData.cvv,
        cardType: this.cardData.type,
        cardBrand: this.detectedCardType
      };

      this.$emit('confirm', paymentData)
    },

    // 关闭结果弹窗
    closeResult() {
      this.showResult = false;
    },

    // 重置表单
    resetForm() {
      this.cardData = {
        number: '',
        holder: '',
        expiryMonth: '',
        expiryYear: '',
        cvv: '',
        type: ''
      };
      this.detectedCardType = '';
    }
  }
};
</script>

<style lang="less" scoped>
.payment-container {
  /* max-width: 500px; */
  margin: 0 14px;
  padding: 14px;
  font-family: 'Arial', sans-serif;
  .payment-form {
    background: #f9f9f9;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  }

  .form-group {
    margin-bottom: 20px;
  }

  .form-row {
    display: flex;
    gap: 15px;
    padding: 0;
  }

  .form-row .form-group {
    flex: 1;
  }

  label {
    display: block;
    margin-bottom: 5px;
    font-weight: bold;
    color: #333;
  }

  input,
  select {
    width: 100%;
    padding: 20px 10px;
    border: 2px solid #ddd;
    border-radius: 8px;
    font-size: 28px;
    transition: border-color 0.3s;
    box-sizing: border-box;
  }

  input:focus,
  select:focus {
    outline: none;
    border-color: #007bff;
  }

  input.error,
  select.error {
    border-color: #dc3545;
  }

  .error-message {
    color: #dc3545;
    font-size: 20px;
    margin-top: 5px;
    min-height: 20px;
  }

  .card-icons {
    display: flex;
    gap: 10px;
    margin-top: 10px;
  }

  .card-icon {
    padding: 5px 10px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 20px;
    font-weight: bold;
    color: #666;
    background: white;
    transition: all 0.3s;
  }

  .card-icon.active {
    border-color: #007bff;
    color: #007bff;
    background: #e7f3ff;
  }

  .expiry-inputs {
    display: flex;
    gap: 10px;
  }

  .expiry-inputs select {
    flex: 1;
  }

  .submit-btn {
    width: 100%;
    padding: 24px;
    background: #007bff;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 28px;
    font-weight: bold;
    cursor: pointer;
    transition: background 0.3s;
    margin-top: 40px;
  }

  .submit-btn:hover:not(:disabled) {
    background: #0056b3;
  }

  .submit-btn:disabled {
    background: #6c757d;
    cursor: not-allowed;
  }

  .submit-btn.processing {
    background: #17a2b8;
  }

  /* 模态框样式 */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .modal {
    background: white;
    padding: 30px;
    border-radius: 8px;
    text-align: center;
    max-width: 400px;
    width: 90%;
  }

  .modal h3 {
    margin-bottom: 15px;
    color: #28a745;
  }

  .modal-btn {
    padding: 10px 20px;
    background: #007bff;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
  }
}

</style>
